import React, { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";

import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import Home from "@/pages/Home";
import NotFound from "@/pages/NotFound";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Support from "@/pages/Support";
import Contact from "@/pages/Contact";
import { SiteSettingsProvider } from "@/hooks/useSiteSettings";
import { WebsiteContentProvider } from "@/hooks/useWebsiteContent";
import { startVersionSync } from "@/lib/versionSync";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import Analytics from "@/components/Analytics";
import SiteSchemas from "@/components/site/SiteSchemas";

// SEO Studio admin UI
import SeoLayout from "@/pages/seo-studio/SeoLayout";
import SeoLogin from "@/pages/seo-studio/SeoLogin";
import Dashboard from "@/pages/seo-studio/Dashboard";
import BriefList from "@/pages/seo-studio/BriefList";
import BriefDetails from "@/pages/seo-studio/BriefDetails";
import { RunList, RunDetails } from "@/pages/seo-studio/Runs";
import ContentPlans from "@/pages/seo-studio/ContentPlans";
import MarketResearch from "@/pages/seo-studio/MarketResearch";
import KeywordResearch from "@/pages/seo-studio/KeywordResearch";
import SearchIntent from "@/pages/seo-studio/SearchIntent";
import OpsDashboard from "@/pages/seo-studio/OpsDashboard";
import OpsConfig from "@/pages/seo-studio/OpsConfig";
import ReviewQueue from "@/pages/seo-studio/ReviewQueue";
import AISettings from "@/pages/seo-studio/AISettings";
import Batches from "@/pages/seo-studio/Batches";
import OpsCosts from "@/pages/seo-studio/OpsCosts";
import OpsHealth from "@/pages/seo-studio/OpsHealth";
import OpsLogs from "@/pages/seo-studio/OpsLogs";
import ErrorCenter from "@/pages/seo-studio/ErrorCenter";
import Reports from "@/pages/seo-studio/Reports";
import Leads from "@/pages/seo-studio/Leads";
import PublishedArticles from "@/pages/seo-studio/PublishedArticles";
import BriefEditor from "@/pages/seo-studio/BriefEditor";

// Public Blog
import BlogList from "@/pages/blog/BlogList";
import BlogArticle from "@/pages/blog/BlogArticle";

// Public Pillar Guides
import GuideArticle from "@/pages/guides/GuideArticle";

// Public Verticals (hub pages)
import VerticalsIndex from "@/pages/verticals/VerticalsIndex";
import VerticalHub from "@/pages/verticals/VerticalHub";

// Hard redirect helper: `/admin` was historically the Payload CMS URL, but the
// CMS now lives at `/api/cms/admin` (only `/api/*` reaches the backend proxy
// via the ingress). Preserve the old link by bouncing the browser there.
function AdminRedirect() {
  useEffect(() => {
    window.location.replace("/api/cms/admin");
  }, []);
  return null;
}

function ScrollToTop() {
  const { pathname, hash, search } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    // Trailing-slash normalization: /about/ → /about (SEO duplicate guard)
    if (pathname.length > 1 && pathname.endsWith("/")) {
      navigate(pathname.replace(/\/+$/, "") + search + hash, { replace: true });
      return;
    }
    if (hash) return; // let hash-links resolve inside pages
    window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [pathname, hash, search, navigate]);
  return null;
}

function Shell() {
  return (
    <div className="min-h-screen flex flex-col bg-white text-[#1D1D1F]">
      <Analytics />
      <SiteSchemas />
      <Nav />
      <div className="flex-1">
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/services" element={<Services />} />
          <Route path="/support" element={<Support />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/blog" element={<BlogList />} />
          <Route path="/blog/:slug" element={<BlogArticle />} />
          <Route path="/guides/:slug" element={<GuideArticle />} />
          <Route path="/verticals" element={<VerticalsIndex />} />
          <Route path="/verticals/:slug" element={<VerticalHub />} />
          <Route path="/admin" element={<AdminRedirect />} />
          <Route path="/admin/*" element={<AdminRedirect />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
      </div>
      <Footer />
    </div>
  );
}

function RootRouter() {
  return (
    <Routes>
      {/* SEO Studio admin — standalone shell, no site Nav/Footer */}
      <Route path="/seo-studio/login" element={<SeoLogin />} />
      <Route path="/seo-studio" element={<SeoLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="briefs" element={<BriefList />} />
        <Route path="briefs/new" element={<BriefEditor />} />
        <Route path="briefs/:briefId/edit" element={<BriefEditor />} />
        <Route path="briefs/:briefId" element={<BriefDetails />} />
        <Route path="runs" element={<RunList />} />
        <Route path="runs/:runId" element={<RunDetails />} />
        <Route path="published" element={<PublishedArticles />} />
        <Route path="review" element={<ReviewQueue />} />
        <Route path="plans" element={<ContentPlans />} />
        <Route path="market" element={<MarketResearch />} />
        <Route path="keywords" element={<KeywordResearch />} />
        <Route path="intents" element={<SearchIntent />} />
        <Route path="ops" element={<OpsDashboard />} />
        <Route path="config" element={<OpsConfig />} />
        <Route path="ai" element={<AISettings />} />
        <Route path="batches" element={<Batches />} />
        <Route path="costs" element={<OpsCosts />} />
        <Route path="health" element={<OpsHealth />} />
        <Route path="logs" element={<OpsLogs />} />
        <Route path="errors" element={<ErrorCenter />} />
        <Route path="reports" element={<Reports />} />
        <Route path="leads" element={<Leads />} />
      </Route>
      {/* Public MTS website — everything else */}
      <Route path="/*" element={<Shell />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    // Runtime cache-version sync: polls /api/health/cms and wipes local
    // caches when the CMS reports a new contentVersion (edited in admin,
    // re-seeded, or otherwise touched). Fully event-driven — no manual
    // seed step required to invalidate the frontend cache.
    return startVersionSync();
  }, []);
  return (
    <div className="App">
      <HelmetProvider>
        <SiteSettingsProvider>
          <WebsiteContentProvider>
            <BrowserRouter>
              <ScrollToTop />
              <RootRouter />
              <Toaster position="top-right" richColors />
            </BrowserRouter>
          </WebsiteContentProvider>
        </SiteSettingsProvider>
      </HelmetProvider>
    </div>
  );
}
